import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { DollarSign, TrendingUp, TrendingDown, Search, Globe2 } from "lucide-react";
import { toast } from "sonner";
import ClientPicker from "@/components/ClientPicker";
import { useAuth } from "@/context/AuthContext";

export default function PricingMemoryPage() {
    const { user } = useAuth();
    const isSuperAdmin = (user?.role || "").toLowerCase() === "super_admin";
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [clientId, setClientId] = useState("");
    const [q, setQ] = useState("");
    const [sortKey, setSortKey] = useState("occurrences");
    const [sortDir, setSortDir] = useState("desc");
    const [crossClient, setCrossClient] = useState(false);
    const [crossCode, setCrossCode] = useState("");
    const [crossLabel, setCrossLabel] = useState("");

    const load = useCallback(async () => {
        if (crossClient) {
            if (!crossCode.trim() && !crossLabel.trim()) {
                toast.error("Enter a procedure code or label to query across clients");
                return;
            }
            setLoading(true);
            try {
                const params = new URLSearchParams();
                if (crossCode.trim()) params.set("procedure_code", crossCode.trim());
                if (crossLabel.trim()) params.set("procedure_label", crossLabel.trim());
                const { data } = await api.get(
                    `/admin/study-assets/procedure-memory-cross?${params.toString()}`,
                );
                setItems(data.items || []);
            } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
            finally { setLoading(false); }
            return;
        }
        if (!clientId.trim()) {
            setItems([]); setLoading(false); return;
        }
        setLoading(true);
        try {
            const { data } = await api.get(
                `/admin/study-assets/procedure-memory?client_id=${encodeURIComponent(clientId.trim())}`,
            );
            setItems(data.items || []);
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        finally { setLoading(false); }
    }, [crossClient, crossCode, crossLabel, clientId]);

    useEffect(() => { load(); }, [load]);
    // Reset items when toggling modes so we don't show stale cross-client rows in single mode (or vice versa).
    useEffect(() => { setItems([]); }, [crossClient]);

    const sorted = React.useMemo(() => {
        const filtered = q.trim()
            ? items.filter((i) => (
                (i.procedure_code || "").toLowerCase().includes(q.toLowerCase())
                || (i.procedure_label || "").toLowerCase().includes(q.toLowerCase())
            ))
            : items;
        const dir = sortDir === "desc" ? -1 : 1;
        return [...filtered].sort((a, b) => {
            const av = a[sortKey] ?? 0;
            const bv = b[sortKey] ?? 0;
            if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
            return String(av).localeCompare(String(bv)) * dir;
        });
    }, [items, q, sortKey, sortDir]);

    const sortBy = (key) => {
        if (sortKey === key) setSortDir(sortDir === "desc" ? "asc" : "desc");
        else { setSortKey(key); setSortDir("desc"); }
    };

    const totals = React.useMemo(() => {
        if (sorted.length === 0) return null;
        const n = sorted.length;
        const obs = sorted.reduce((s, r) => s + (r.occurrences || 0), 0);
        const median_sum = sorted.reduce((s, r) => s + (r.median_amount_usd || 0), 0);
        return { n, obs, avg_median: median_sum / n };
    }, [sorted]);

    return (
        <div className="px-8 py-8" data-testid="pricing-memory-page">
            <div className="flex items-start justify-between mb-6">
                <div>
                    <div className="label-xs text-[#7C3AED]">Clinical Operations</div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">Pricing Memory</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        Per-client procedure pricing history. Auto-updated on every successful budget ingest.
                        Median + range + occurrences let you spot pricing drift across studies.
                    </p>
                </div>
            </div>

            <div className="flex items-center gap-3 mb-3 flex-wrap">
                {isSuperAdmin && (
                    <button
                        type="button"
                        onClick={() => setCrossClient((v) => !v)}
                        data-testid="pm-cross-toggle"
                        className={`mt-5 px-3 py-2 rounded-md text-xs font-medium border flex items-center gap-1.5 transition ${
                            crossClient
                                ? "bg-[#7C3AED]/15 border-[#7C3AED]/40 text-[#c7a6ff]"
                                : "bg-white/5 border-white/10 text-white/65 hover:bg-white/10"
                        }`}
                    >
                        <Globe2 className="w-3.5 h-3.5" /> {crossClient ? "Cross-client (super admin)" : "Single client"}
                    </button>
                )}
                {!crossClient && (
                    <div className="flex-1 min-w-[260px] max-w-md" data-testid="pm-client-picker-wrap">
                        <ClientPicker
                            value={clientId} onChange={(v) => setClientId(v)}
                            label="Client" excludeDisabled
                            testId="pm-client-id"
                        />
                    </div>
                )}
                {crossClient && (
                    <>
                        <div className="flex-1 min-w-[180px] max-w-xs">
                            <label className="text-xs text-white/40 mb-1 block">Procedure code</label>
                            <input
                                value={crossCode} onChange={(e) => setCrossCode(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && load()}
                                placeholder="e.g. CPT-99213"
                                data-testid="pm-cross-code"
                                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono"
                            />
                        </div>
                        <div className="flex-1 min-w-[200px] max-w-md">
                            <label className="text-xs text-white/40 mb-1 block">…or label substring</label>
                            <input
                                value={crossLabel} onChange={(e) => setCrossLabel(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && load()}
                                placeholder="e.g. ECG, CBC, dispensing fee"
                                data-testid="pm-cross-label"
                                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                            />
                        </div>
                    </>
                )}
                <button
                    onClick={load}
                    data-testid="pm-load-btn"
                    className="mt-5 px-4 py-2 rounded-full bg-[#7C3AED] hover:bg-[#9667ee] text-white text-sm"
                >Load</button>
                <div className="flex-1 max-w-sm relative mt-5">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                        value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder="Filter rows…"
                        data-testid="pm-filter"
                        className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm"
                    />
                </div>
            </div>

            {totals && (
                <div className="grid grid-cols-3 gap-3 mb-4">
                    <Stat label="Procedures tracked" value={totals.n} />
                    <Stat label="Total observations" value={totals.obs} />
                    <Stat label="Avg of medians" value={`$${Math.round(totals.avg_median).toLocaleString()}`} />
                </div>
            )}

            <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="pm-table">
                {loading ? <div className="p-8 text-white/50">Loading…</div>
                    : !crossClient && !clientId.trim() ? (
                        <div className="p-12 text-center text-white/50" data-testid="pm-empty-no-client">
                            <DollarSign className="w-10 h-10 mx-auto mb-3 opacity-40" />
                            Pick a client to view pricing history.
                        </div>
                    ) : crossClient && !crossCode.trim() && !crossLabel.trim() ? (
                        <div className="p-12 text-center text-white/50" data-testid="pm-empty-cross">
                            <Globe2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
                            Enter a procedure code or label to query across all clients.
                        </div>
                    ) : sorted.length === 0 ? (
                        <div className="p-12 text-center text-white/50">No procedures found.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-white/[0.02] text-white/50">
                                <tr>
                                    {crossClient && (
                                        <Th label="Client" k="client_id" sortKey={sortKey} sortDir={sortDir} onSort={sortBy} />
                                    )}
                                    <Th label="Code" k="procedure_code" sortKey={sortKey} sortDir={sortDir} onSort={sortBy} />
                                    <Th label="Procedure" k="procedure_label" sortKey={sortKey} sortDir={sortDir} onSort={sortBy} />
                                    <Th label="Occurrences" k="occurrences" sortKey={sortKey} sortDir={sortDir} onSort={sortBy} align="right" />
                                    <Th label="Median" k="median_amount_usd" sortKey={sortKey} sortDir={sortDir} onSort={sortBy} align="right" />
                                    <Th label="Min" k="min_amount_usd" sortKey={sortKey} sortDir={sortDir} onSort={sortBy} align="right" />
                                    <Th label="Max" k="max_amount_usd" sortKey={sortKey} sortDir={sortDir} onSort={sortBy} align="right" />
                                    <Th label="Last seen" k="last_seen_at" sortKey={sortKey} sortDir={sortDir} onSort={sortBy} />
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map((r) => (
                                    <tr key={`${r.client_id || ""}::${r.id || r.procedure_code}`} data-testid={`pm-row-${r.procedure_code}`}
                                        className="border-t border-white/5 hover:bg-white/[0.02]">
                                        {crossClient && (
                                            <td className="px-4 py-2 font-mono text-xs text-[#c7a6ff]">{r.client_id}</td>
                                        )}
                                        <td className="px-4 py-2 font-mono text-xs text-white/70">{r.procedure_code}</td>
                                        <td className="px-4 py-2 max-w-md truncate" title={r.procedure_label}>
                                            {r.procedure_label}
                                        </td>
                                        <td className="px-4 py-2 text-right text-white/70 tabular-nums">
                                            {r.occurrences}
                                            {r.occurrences >= 3 && <span className="ml-1 text-[10px] text-emerald-400">●</span>}
                                            {r.occurrences === 2 && <span className="ml-1 text-[10px] text-amber-400">●</span>}
                                            {r.occurrences === 1 && <span className="ml-1 text-[10px] text-white/30">●</span>}
                                        </td>
                                        <td className="px-4 py-2 text-right tabular-nums text-emerald-400">
                                            ${(r.median_amount_usd || 0).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-2 text-right tabular-nums text-white/50">
                                            ${(r.min_amount_usd || 0).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-2 text-right tabular-nums text-white/50">
                                            ${(r.max_amount_usd || 0).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-2 text-white/40 text-xs">
                                            {(r.last_seen_at || "").slice(0, 10)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
            </div>
            <p className="text-xs text-white/40 mt-3">
                Confidence indicator: <span className="text-emerald-400">●</span> high (≥3 obs) ·{" "}
                <span className="text-amber-400">●</span> medium (2 obs) ·{" "}
                <span className="text-white/30">●</span> low (1 obs)
            </p>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <div className="text-xs text-white/40">{label}</div>
            <div className="mt-1 text-xl font-medium tabular-nums">{value}</div>
        </div>
    );
}

function Th({ label, k, sortKey, sortDir, onSort, align = "left" }) {
    const active = sortKey === k;
    return (
        <th
            onClick={() => onSort(k)}
            className={`px-4 py-3 font-normal text-${align} cursor-pointer select-none ${active ? "text-white" : ""}`}
        >
            {label}
            {active && (
                <span className="ml-1 text-[10px]">{sortDir === "desc" ? "↓" : "↑"}</span>
            )}
        </th>
    );
}
